/*! Rapport — the CRM that remembers every customer — © 2026 Hasib (OVERSHOOT). All rights reserved. Licensed to capitalead26@gmail.com (order INS-A1FF01B1), not sold: see LICENSE.md. */
/* Portage React du vocabulaire d'animation du thème (src/motion.ts) : GSAP gère toutes les transformations,
   chaque durée passe par dur() qui vaut 0 si l'utilisateur demande moins d'animations. */
import gsap from "gsap";
import { Flip } from "gsap/Flip";

gsap.registerPlugin(Flip);
export { gsap, Flip };

export const reducedMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
export const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
export const dur = (d) => (reducedMQ.matches ? 0 : d);
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

export function split(el) {
  const src = el.dataset.text ?? el.innerHTML.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "");
  if (el.dataset.split === src) return $$(".ch", el);
  const text = src.replace(/&amp;/g, "&").trim();
  el.setAttribute("aria-label", text.replace(/\n/g, " "));
  el.innerHTML = text.split("\n").map((line) => line.trim().split(" ").map((w) => `<span class="word" aria-hidden="true">${[...w].map((c) => `<span class="ch">${esc(c)}</span>`).join("")}</span>`).join(" ")).join("<br>");
  el.dataset.split = src;
  return $$(".ch", el);
}

export function riseTitle(el, delay = 0) {
  if (!el) return gsap.timeline();
  return gsap.fromTo(split(el), { yPercent: 115, rotation: 6 }, { yPercent: 0, rotation: 0, duration: dur(0.85), ease: "power4.out", stagger: dur(0.022), delay: dur(delay), clearProps: "transform" });
}

export function landCards(cards, { y = 34, stagger = 0.07, delay = 0 } = {}) {
  if (!cards.length) return gsap.timeline();
  return gsap.fromTo(cards, { opacity: 0, y, scale: 0.985 }, { opacity: 1, y: 0, scale: 1, duration: dur(0.9), ease: "power3.out", stagger: dur(stagger), delay: dur(delay), clearProps: "transform,opacity" });
}

export function fileIn(els, { x = 0, y = 12, stagger = 0.045, delay = 0 } = {}) {
  if (!els.length) return gsap.timeline();
  return gsap.fromTo(els, { opacity: 0, x, y }, { opacity: 1, x: 0, y: 0, duration: dur(0.55), ease: "power3.out", stagger: dur(stagger), delay: dur(delay), clearProps: "transform,opacity" });
}

export function deal(tiles, { delay = 0, stagger = 0.06 } = {}) {
  if (!tiles.length) return gsap.timeline();
  return gsap.fromTo(tiles, { opacity: 0, y: 40, rotation: (i) => (i % 2 ? 3 : -3), scale: 0.94 }, { opacity: 1, y: 0, rotation: 0, scale: 1, duration: dur(0.8), ease: "back.out(1.4)", stagger: dur(stagger), delay: dur(delay), clearProps: "transform,opacity" });
}

export function pop(el, { from = 0.6, delay = 0, stagger = 0.05 } = {}) {
  if (!el || reducedMQ.matches) return;
  gsap.fromTo(el, { scale: from, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.6, ease: "back.out(3)", delay, stagger, overwrite: "auto", clearProps: "transform,opacity" });
}

export function bump(el) {
  if (!el) return;
  el.classList.remove("is-flash");
  void el.offsetWidth;
  el.classList.add("is-flash");
  if (reducedMQ.matches) return;
  gsap.fromTo(el, { y: -4, scale: 1.06 }, { y: 0, scale: 1, duration: 0.55, ease: "back.out(2.4)", overwrite: "auto", clearProps: "transform" });
}

export function floatUp(anchor, text) {
  if (!anchor || reducedMQ.matches) return;
  const r = anchor.getBoundingClientRect();
  const el = Object.assign(document.createElement("span"), { className: "float-up", textContent: text });
  el.setAttribute("aria-hidden", "true");
  Object.assign(el.style, { left: `${r.left + r.width / 2}px`, top: `${r.top}px` });
  document.body.append(el);
  gsap.fromTo(el, { opacity: 0, y: 6, xPercent: -50, scale: 0.9 }, { keyframes: [{ opacity: 1, y: -12, scale: 1, duration: 0.35, ease: "back.out(2)" }, { opacity: 0, y: -40, duration: 0.8, ease: "power2.in", delay: 0.6 }], onComplete: () => el.remove() });
}

const counts = new WeakMap();
export function countTo(el, to, format, { duration = 1.2, delay = 0, from } = {}) {
  if (!el) return;
  let st = counts.get(el);
  if (!st) counts.set(el, (st = { v: from ?? 0 }));
  if (from !== undefined) st.v = from;
  gsap.killTweensOf(st);
  if (reducedMQ.matches || !duration || st.v === to) { st.v = to; el.textContent = format(to); return; }
  el.textContent = format(st.v);
  gsap.to(st, { v: to, duration, delay, ease: "power3.out", onUpdate: () => { el.textContent = format(st.v); }, onComplete: () => { el.textContent = format(to); } });
}

export function squish(btn) {
  if (reducedMQ.matches) return;
  gsap.fromTo(btn.querySelector("svg") ?? btn, { scale: 0.72 }, { scale: 1, duration: 0.55, ease: "back.out(3)", overwrite: true });
}

export function magnetic(root = document) {
  if (!fine.matches) return;
  $$(".magnetic", root).forEach((b) => {
    if (b.__mag) return;
    b.__mag = true;
    const qx = gsap.quickTo(b, "x", { duration: 0.5, ease: "power3.out" });
    const qy = gsap.quickTo(b, "y", { duration: 0.5, ease: "power3.out" });
    b.addEventListener("pointermove", (e) => {
      if (reducedMQ.matches) return;
      const r = b.getBoundingClientRect();
      qx((e.clientX - r.left - r.width / 2) * 0.24);
      qy((e.clientY - r.top - r.height / 2) * 0.3);
    });
    b.addEventListener("pointerleave", () => { qx(0); qy(0); });
  });
}

export const settled = { v: false };
export function hoverLift(root = document, by = -4) {
  if (!fine.matches) return;
  $$("[data-lift]", root).forEach((c) => {
    if (c.__lift) return;
    c.__lift = true;
    c.addEventListener("pointerenter", () => { if (!reducedMQ.matches && settled.v) gsap.to(c, { y: by, duration: 0.45, ease: "power3.out", overwrite: "auto" }); });
    c.addEventListener("pointerleave", () => { if (settled.v) gsap.to(c, { y: 0, duration: 0.5, ease: "power3.out", overwrite: "auto", clearProps: "transform" }); });
  });
}

export function driftMesh(root = document) {
  if (reducedMQ.matches) return () => {};
  const tweens = $$(".mesh i", root).map((m, i) => gsap.to(m, { x: [60, -50, 40][i], y: [40, -60, 50][i], scale: [1.08, 0.94, 1.1][i], duration: 14 + i * 4, ease: "sine.inOut", yoyo: true, repeat: -1 }));
  return () => tweens.forEach((t) => t.kill());
}

export function slidePill(pill, track, tab, animate = true) {
  if (!pill || !track) return;
  if (!tab) { gsap.to(pill, { opacity: 0, duration: dur(0.2) }); return; }
  gsap.to(pill, { x: tab.offsetLeft, y: tab.offsetTop, width: tab.offsetWidth, height: tab.offsetHeight, opacity: 1, duration: animate ? dur(0.5) : 0, ease: "power3.inOut", overwrite: true });
}

/* Entrée d'une page : titre, cartes, tuiles colorées, rangées, puis survols et boutons magnétiques une fois posés. */
export function enterPage(root) {
  if (!root) return () => {};
  settled.v = false;
  const tl = gsap.timeline();
  root.querySelectorAll("[data-rise]").forEach((h) => tl.add(riseTitle(h), 0));
  // seuls les éléments visibles à l'écran sont animés : une colonne de 200 cartes ne doit pas attendre son tour
  const onScreen = (sel, max) => $$(sel, root).filter((el) => el.getBoundingClientRect().top < window.innerHeight).slice(0, max);
  tl.add(landCards(onScreen("[data-land]", 12)), 0.08);
  tl.add(deal(onScreen("[data-deal]", 28), { delay: 0.25, stagger: 0.03 }), 0.1);
  tl.add(fileIn(onScreen("[data-file]", 20), { delay: 0.3 }), 0.1);
  root.querySelectorAll("[data-count]").forEach((el) => countTo(el, Number(el.dataset.count), (v) => Math.round(v).toLocaleString("fr-FR"), { delay: 0.2 }));
  magnetic(root);
  hoverLift(root);
  const t = setTimeout(() => { settled.v = true; }, reducedMQ.matches ? 0 : 1100);
  // StrictMode démonte/remonte sans retirer le DOM : on ne coupe l'animation que si la vue a vraiment disparu
  return () => { clearTimeout(t); if (!root.isConnected) tl.kill(); };
}
